import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribe } from 'lib/pusher';
import { downloadFromUrl } from 'utils/download-blob';

/** Shape of the completion event the server publishes over Pusher. */
interface ExportCompleteEvent {
  export_id?: string;
  exportId?: string;
  status: 'complete' | 'failed';
  download_url?: string;
  downloadUrl?: string;
  row_count?: number;
  rowCount?: number;
  error?: string;
}

/**
 * Status of a server-side export operation.
 */
export interface ServerExportStatus {
  /** Unique export ID returned by the server */
  exportId: string | null;
  /** Current status of the export */
  status: 'idle' | 'processing' | 'complete' | 'failed';
  /** Signed download URL (available when status is 'complete') */
  downloadUrl: string | null;
  /** Error message (available when status is 'failed') */
  error: string | null;
  /** Number of rows exported (available when status is 'complete') */
  rowCount: number | null;
}

/**
 * Parameters for starting an export.
 * These are passed to your exportFn.
 */
export interface ExportParams {
  filters?: Record<string, unknown>;
  sort?: { column: string; direction: string };
  columns?: string[];
  [key: string]: unknown;
}

/**
 * Configuration for the useServerExport hook.
 */
export interface UseServerExportConfig {
  /**
   * Pusher channel to subscribe to for completion notifications. Pass `null` when there is
   * nothing to export yet (e.g. no record selected) — the hook then skips the subscription
   * rather than binding a placeholder channel.
   */
  channel: string | null;
  /** Pusher event name for export completion */
  event: string;
  /**
   * Function that calls your export API endpoint.
   * Should return the exportId from the server response.
   */
  exportFn: (params?: ExportParams) => Promise<{ exportId: string }>;
  /** Whether to automatically open the download URL when export completes (default: true) */
  autoDownload?: boolean;
  /**
   * Optional backstop, in milliseconds, for the Pusher-only completion event. Delivery is
   * Pusher-only with no server poll, so a job that is killed or whose event is lost would
   * otherwise leave the UI on "processing" forever. When set, the hook flips to `failed` after
   * this long still processing. Omit (the default) to wait indefinitely, preserving the prior
   * behavior for existing callers. Set it generously — long legitimate exports must not trip it.
   */
  timeoutMs?: number;
}

/**
 * Return value from the useServerExport hook.
 */
export interface UseServerExportReturn {
  /** Current export status */
  exportStatus: ServerExportStatus;
  /** Start a new export with optional parameters */
  startExport: (params?: ExportParams) => Promise<void>;
  /** Reset export status to idle */
  resetExport: () => void;
  /** Whether an export is currently in progress */
  isExporting: boolean;
}

const INITIAL_STATUS: ServerExportStatus = {
  exportId: null,
  status: 'idle',
  downloadUrl: null,
  error: null,
  rowCount: null,
};

/**
 * Hook for handling server-side exports with Pusher notifications.
 *
 * This hook provides a standard interface for:
 * 1. Starting an export via your API endpoint
 * 2. Subscribing to Pusher for completion notifications
 * 3. Tracking export status (idle, processing, complete, failed)
 * 4. Auto-downloading the file when complete (optional)
 *
 * @return {UseServerExportReturn} The export status and control functions
 * @example
 * ```tsx
 * const { exportStatus, startExport, isExporting } = useServerExport({
 *   channel: `package_${packageId}`,
 *   event: 'slot_allocations_export_complete',
 *   exportFn: async (params) => {
 *     const response = await fetch(`/packages/${packageId}/enhanced_slot_allocations/export`, {
 *       method: 'POST',
 *       headers: { 'Content-Type': 'application/json' },
 *       body: JSON.stringify(params),
 *     });
 *     const data = await response.json();
 *     return { exportId: data.exportId };
 *   },
 * });
 *
 * // Start export when user clicks button
 * <Button onClick={() => startExport({ filters: currentFilters })} disabled={isExporting}>
 *   {isExporting ? 'Exporting...' : 'Export'}
 * </Button>
 * ```
 */
export const useServerExport = ({
  channel,
  event,
  exportFn,
  autoDownload = true,
  timeoutMs,
}: UseServerExportConfig): UseServerExportReturn => {
  const [exportStatus, setExportStatus] =
    useState<ServerExportStatus>(INITIAL_STATUS);

  // Our export's id, mirrored into a ref so the Pusher handler reads the current
  // value without the effect re-binding on every id change. Written the instant
  // the start POST resolves — earlier than React commits the state.
  const exportIdRef = useRef<string | null>(null);
  // Events that arrive before the start POST returns our id (a fast export can
  // publish first), keyed by id. The one matching our id is applied once it
  // lands; others are ignored, since the channel is shared across exports.
  const bufferedEventsRef = useRef<Map<string, ExportCompleteEvent>>(new Map());

  // Applies a completion/failure event to state (and auto-downloads on success).
  const applyEvent = useCallback(
    (data: ExportCompleteEvent) => {
      if (data.status === 'complete') {
        const downloadUrl = data.download_url || data.downloadUrl || null;
        const rowCount = data.row_count ?? data.rowCount ?? null;
        setExportStatus({
          exportId: exportIdRef.current,
          status: 'complete',
          downloadUrl,
          error: null,
          rowCount,
        });
        // Same-tab helper, not window.open: this runs from a Pusher callback with no
        // user activation (popup-blocked), and export SAS URLs are Content-Disposition:
        // attachment, so the browser saves in place.
        if (autoDownload && downloadUrl) {
          downloadFromUrl(downloadUrl);
        }
      } else if (data.status === 'failed') {
        setExportStatus({
          exportId: exportIdRef.current,
          status: 'failed',
          downloadUrl: null,
          error: data.error || 'Export failed',
          rowCount: null,
        });
      }
    },
    [autoDownload]
  );

  // Reset export status to idle
  const resetExport = useCallback(() => {
    exportIdRef.current = null;
    bufferedEventsRef.current.clear();
    setExportStatus(INITIAL_STATUS);
  }, []);

  // Start a new export
  const startExport = useCallback(
    async (params?: ExportParams) => {
      try {
        exportIdRef.current = null;
        bufferedEventsRef.current.clear();
        setExportStatus({
          exportId: null,
          status: 'processing',
          downloadUrl: null,
          error: null,
          rowCount: null,
        });

        const { exportId } = await exportFn(params);

        exportIdRef.current = exportId;
        setExportStatus((prev) => ({
          ...prev,
          exportId,
        }));

        // A fast/empty export may have completed before this POST resolved;
        // apply that buffered event now that we know our id. Other ids buffered
        // meanwhile can never match, so drop them too.
        const buffered = bufferedEventsRef.current.get(exportId);
        bufferedEventsRef.current.clear();
        if (buffered) applyEvent(buffered);
      } catch (err) {
        exportIdRef.current = null;
        bufferedEventsRef.current.clear();
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to start export';
        setExportStatus({
          exportId: null,
          status: 'failed',
          downloadUrl: null,
          error: errorMessage,
          rowCount: null,
        });
      }
    },
    [exportFn, applyEvent]
  );

  // Subscribe to Pusher for export completion. Bound once while processing (the
  // handler reads exportIdRef, so it never re-binds as the id resolves).
  useEffect(() => {
    if (exportStatus.status !== 'processing' || !channel) {
      return;
    }

    const subscription = subscribe(channel);
    const bufferedEvents = bufferedEventsRef.current;

    const handleExportComplete = (data: ExportCompleteEvent) => {
      // Normalize snake_case and camelCase from server
      const notificationExportId = data.export_id || data.exportId;
      if (!notificationExportId) {
        return;
      }

      // Accept only our own export's event. If our id isn't known yet (start
      // POST in flight), buffer by id — it may be ours arriving early, and the
      // channel is shared, so we must not act on it until the id matches.
      if (notificationExportId === exportIdRef.current) {
        applyEvent(data);
      } else if (!exportIdRef.current) {
        bufferedEvents.set(notificationExportId, data);
      }
    };

    subscription.bind(event, handleExportComplete);

    // Backstop for a lost/killed completion event (see `timeoutMs`). Scoped to this
    // processing cycle: cleared when the status leaves 'processing' (success, failure, or
    // reset) or on unmount, so it only fires while we are genuinely still waiting.
    const timeoutId =
      timeoutMs === undefined
        ? undefined
        : setTimeout(() => {
            setExportStatus((prev) =>
              prev.status === 'processing'
                ? {
                    ...prev,
                    status: 'failed',
                    error:
                      // Generic across every surface (some have a single dataset), and it does NOT
                      // promise a later result: once this timer fires the Pusher handler is unbound,
                      // so telling the user to "check back" would point nowhere.
                      'This export is taking longer than expected and may have failed. Please try again with a narrower search.',
                  }
                : prev
            );
          }, timeoutMs);

    return () => {
      subscription.unbind(event, handleExportComplete);
      bufferedEvents.clear();
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [channel, event, exportStatus.status, applyEvent, timeoutMs]);

  return {
    exportStatus,
    startExport,
    resetExport,
    isExporting: exportStatus.status === 'processing',
  };
};

export default useServerExport;
